<template>
  <v-data-table
    :aria-label="$t('L_policy_coverage_details')"
    :items="data"
    hide-default-footer
    hide-default-header
    class="elevation-0 bg-white"
  >
    <template v-slot:item="props">
      <tr :class="index % 2 === 0 ? 'primarylight2' : 'white'">
        <td
          v-for="(header, headerIndex) in headers"
          :key="headerIndex + 'insured'"
          class="pl-2 pt-2 valign-top"
          :class="props.item[header.value]?.itemClass ?? ''"
        >
          <div
            :class="[
              { clickable: props?.item[header.value]?.clickable },
              props?.item[header.value]?.class,
            ]"
          >
            <!-- ACCOUNT NUMBER -->
            <div v-if="header.value === 'accountNumber'" class="account">
              <div class="link">
                {{ props.item.accountNumber }}
                <v-icon
                  @click="accountNumberClicked(props.item)"
                  v-if="allowViewAccountNumber"
                  id="link"
                  aria-hidden="true"
                  class="v-icon notranslate v-theme--primerica mr-2"
                  style="color: #02579e"
                  :size="18"
                  :icon="mdiEye"
                />
              </div>
              <div class="type">
                {{ props.item.accountType }}
              </div>
              <div>
                {{ props.item.firstName + ' ' + props.item.lastName }}
              </div>
              <template v-if="isMobile">
                <div
                  @click="companyNameClicked(props?.item)"
                  :class="[{ linkbny: props.item.companyLink, link: !props.item.companyLink }]"
                >
                  <div id="click" class="type">
                    {{ props.item.companyName }}
                  </div>
                  <v-icon
                    v-if="props.item.companyLink"
                    id="click"
                    aria-hidden="true"
                    class="v-icon v-icon--link material-icons theme--light text-blue-darken-2'"
                    style="color: #02579e"
                    :size="18"
                    :icon="mdiLaunch"
                  />
                </div>
              </template>
            </div>
            <!-- COMPANY NAME -->
            <div
              @click="companyNameClicked(props?.item)"
              v-else-if="header.value === 'companyName'"
              class="account"
            >
              <div
                v-if="!isMobile"
                :class="[{ linkbny: props.item.companyLink, link: !props.item.companyLink }]"
              >
                <span id="click" class="type">{{ props.item.companyName }}</span>
                <v-icon
                  v-if="props.item.companyLink"
                  id="click"
                  :icon="mdiLaunch"
                  aria-hidden="true"
                  class="v-icon v-icon--link material-icons theme--light text-blue-darken-2'"
                  style="color: #02579e"
                  :size="18"
                />
              </div>
            </div>
            <!-- ENDING VALUE -->
            <div v-else-if="header.value === 'endingValue'" class="ending">
              <div class="font-weight-medium">
                {{ currency(props.item.endingValue) }}
              </div>
              <div class="date">{{ $t('L_as_of') }} {{ props.item.asOfDate }}</div>
            </div>
          </div>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { currency } from '@/mixins/numbers';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { FEATURE_FLAGS } from '@/shared/models/constants.models';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapState } from 'pinia';
import { defineComponent, type PropType } from 'vue';
import { mdiEye, mdiLaunch } from '@mdi/js';
import { $t } from '@/i18n/i18n.ts';

export default defineComponent({
  name: 'LinkedCustodianAccounts',
  mixins: [currency],
  props: {
    displayHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
    headers: {
      type: Array as PropType<Array<{ value: string; [key: string]: unknown }>>,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    mdiEye,
    mdiLaunch,
  }),
  computed: {
    ...mapState(useGlobalStore, ['containsFeature']),
    ...mapState(usePolicyStore, {
      isPending: 'isPending',
    }),
    isMobile() {
      return this.$vuetify.display.xs;
    },
    allowViewAccountNumber() {
      return this.containsFeature(FEATURE_FLAGS.VIEW_ACCOUNT_NUMBER);
    },
  },
  methods: {
    $t,
    clicked(e: MouseEvent | null) {
      const targetId = (e?.target as HTMLElement | null)?.id;
      if (targetId === 'click') {
        this.$emit('click');
      } else if (targetId === 'link') {
        this.$emit('link');
      }
    },
    accountNumberClicked(acct: { id?: string } | null) {
      const a = { accountNumber: { id: acct?.id } };
      this.$emit('account-number-clicked', a);
    },
    companyNameClicked(acct: { id?: string } | null) {
      this.$emit('company-name-clicked', acct);
    },
    getHeaders() {
      const th = [].slice.call(this.$el.querySelectorAll('th.column-header'));
      this.adaTable(th);
    },
    adaTable(th: HTMLElement[]) {
      th.forEach((item) => {
        item.removeAttribute('aria-label');
      });
    },
  },
  mounted() {
    this.getHeaders();
  },
});
</script>

<style lang="scss" scoped>
:deep(th.column-header) {
  vertical-align: top;
  padding-top: 9px !important;
}

:deep(.valign-top) {
  vertical-align: top;
}

.linkbny {
  font-weight: 600;
  color: #02579e;
  cursor: pointer;
}

.v-theme--primerica.v-table:not(.v-data-table),
.v-theme--primerica.v-data-table {
  border: none !important;
}
</style>
